import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Image,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
  FilterActions,
  FilterBar,
  FilterField,
} from '../../components/common/FilterBar'
import {
  PaginatedListCard,
  PaginatedListContainer,
  PaginatedListToolbar,
  PAGINATED_LIST_TABLE_SIZE,
  buildPaginatedListPagination,
  renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { getLocalizedApiErrorMessage } from '../../utils/apiErrorI18n'
import { resolveManagedObjectUrls } from '../../services/ossService'
import { PageLayout } from '../../components/layout'
import { usePermissions } from '../../hooks/usePermissions'
import {
  cancelDeviceUpgradeBatch,
  createDeviceUpgradeBatch,
  deleteDeviceUpgradeArtifact,
  disableDeviceUpgradeArtifact,
  enableDeviceUpgradeArtifact,
  extractDeviceUpgradeBatchConflict,
  getDeviceBindings,
  getSelectableUpgradeDevices,
  getDeviceUpgradeArtifacts,
  getDeviceUpgradeBatch,
  getDeviceUpgradeBatchDevices,
  getDeviceUpgradeBatches,
  inspectDeviceUpgradeArtifact,
  registerPrebuiltDeviceUpgradeArtifact,
  syncDeviceUpgradeBatch,
  syncMenderDevices,
  updateDeviceUpgradeArtifactDescription,
} from '../../services/deviceUpgradeService'
import { localeService } from '../../services/localeService'
import { lookupProjects } from '../../services/projectService'
import { getDevicePackageDisplayName } from '../../utils/devicePackageDisplay'
import {
  filterSearchOptionByLabel,
  normalizeSearchText,
} from '../../utils/searchText'
import type {
  DeviceBinding,
  DeviceUpgradeArtifact,
  DeviceUpgradeArtifactMetadata,
  DeviceUpgradeBatch,
  DeviceUpgradeBatchConflictDevice,
  DeviceUpgradeBatchConflictDetail,
  DeviceUpgradeBatchDevice,
  DeviceUpgradeBatchDevicePage,
  SelectableUpgradeDevice,
  SelectableUpgradeDevicePage,
} from '../../types/device-upgrade'
import type { ProjectLookupItem } from '../../types/project'

export type DeviceUpgradeSection = 'bindings' | 'artifacts' | 'batches'

type DeviceUpgradeManagementProps = {
  section?: DeviceUpgradeSection
}

const DEVICE_UPGRADE_SECTION_CONFIG: Record<DeviceUpgradeSection, {
  titleKey: string
  defaultTitle: string
}> = {
  bindings: {
    titleKey: 'menu.deviceUpgradeBindings',
    defaultTitle: '设备绑定',
  },
  artifacts: {
    titleKey: 'menu.deviceUpgradeArtifacts',
    defaultTitle: '升级包管理',
  },
  batches: {
    titleKey: 'menu.deviceUpgradeBatches',
    defaultTitle: '升级批次管理',
  },
}

type ArtifactFormValues = {
  name: string
  package_type: string
  version: string
  device_type: string
  artifact_name?: string
  description?: string
}

type DisableArtifactFormValues = {
  description: string
}

type EditArtifactFormValues = {
  description?: string
}

type BatchFormValues = {
  name: string
  description?: string
  artifact_id: number
  device_ids: string[]
}

type PaginationStateSetter = React.Dispatch<React.SetStateAction<number>>

type SelectFilterValue = string | undefined
type TranslationFunction = ReturnType<typeof useTranslation>['t']

type BindingStatus = 'bound' | 'unbound' | string

type BindingFilters = {
  deviceName: string
  projectId?: SelectFilterValue
  deviceStatus?: SelectFilterValue
  bindingStatus?: SelectFilterValue
}

type ArtifactFilters = {
  keyword: string
  version: string
  status?: SelectFilterValue
}

type BatchFilters = {
  keyword: string
  status?: SelectFilterValue
}

type BatchDeviceFilters = {
  keyword: string
  status?: SelectFilterValue
}

type DevicePickerFilters = {
  keyword: string
  projectIds: number[]
}

const DEFAULT_BINDING_FILTERS: BindingFilters = {
  deviceName: '',
  projectId: 'all',
  deviceStatus: 'all',
  bindingStatus: 'all',
}

const DEFAULT_ARTIFACT_FILTERS: ArtifactFilters = {
  keyword: '',
  version: '',
  status: 'all',
}

const DEFAULT_BATCH_FILTERS: BatchFilters = {
  keyword: '',
  status: undefined,
}

const DEFAULT_BATCH_DEVICE_FILTERS: BatchDeviceFilters = {
  keyword: '',
  status: undefined,
}

const DEFAULT_DEVICE_PICKER_FILTERS: DevicePickerFilters = {
  keyword: '',
  projectIds: [],
}

type BindingListRow = {
  id: string
  rowKey: string
  type?: number | null
  name?: string | null
  status?: number | null
  installation_image_path?: string | null
  current_device_version?: string | null
  package_key?: string | null
  mender_device_id?: string | null
  mender_hardware_id?: string | null
  mender_physical_address?: string | null
  mender_device_type?: string | null
  mender_device_system?: string | null
  mender_last_seen_at?: string | null
  mender_display_seen_at?: string | null
  mender_probe_status?: string | null
  mender_probe_checked_at?: string | null
  mender_probe_error?: string | null
  ota_binding_status?: BindingStatus | null
  ota_current_app_version?: string | null
  ota_current_model_version?: string | null
  ota_last_inventory_sync_at?: string | null
  ota_version_status?: string | null
  ota_version_observed_at?: string | null
  project_id?: number | null
  project_name?: string | null
}

type SelectableDeviceTableRow = SelectableUpgradeDevice & {
  mender_device_system?: string | null
}

const DEFAULT_LOCAL_PAGE_SIZE = 10
const BATCH_NAME_MAX_LENGTH = 128
const DESCRIPTION_MAX_LENGTH = 1000
const ARTIFACT_TABLE_SCROLL_X = 1290
const BINDING_TABLE_SCROLL_X = 1090
const BATCH_TABLE_SCROLL_X = 1360
const BATCH_DEVICE_TABLE_SCROLL_X = 960
const DEVICE_PICKER_TABLE_SCROLL_X = 900
const BATCH_CONFLICT_PREVIEW_LIMIT = 5
const BATCH_CONFLICT_DETAIL_PAGE_SIZE = 10
const SELECTABLE_DEVICE_SYNC_MIN_INTERVAL_MS = 2 * 60 * 1000
const MANUAL_DEVICE_SYNC_BUSY_MS = 10 * 1000
const BATCH_ROW_AUTO_REFRESH_INTERVAL_MS = 30_000
const BATCH_MODAL_BODY_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'min(720px, calc(100vh - 96px))',
  overflowX: 'hidden',
  overflowY: 'auto',
}
const TABLE_ACTION_BUTTON_STYLE: React.CSSProperties = {
  paddingInline: 0,
}
const TERMINAL_BATCH_STATUSES = new Set(['success', 'failed', 'partial_success', 'canceled'])
const AUTO_REFRESH_BATCH_STATUSES = new Set(['pending', 'running', 'sync_failed', 'verifying', 'canceling'])
const SYNCABLE_BATCH_STATUSES = new Set(['pending', 'running', 'sync_failed', 'verifying'])
const PENDING_BATCH_DEVICE_STATUSES = new Set(['pending', 'unknown'])
const RUNNING_BATCH_DEVICE_STATUSES = new Set(['downloading', 'installing', 'rebooting', 'rollback_running'])
const CANCELABLE_BATCH_DEVICE_STATUSES = new Set([
  'pending',
  'downloading',
  'installing',
  'rebooting',
  'verifying',
  'rollback_running',
  'unknown',
])

const DEFAULT_BATCH_DEVICE_PAGE: DeviceUpgradeBatchDevicePage = {
  page: 1,
  page_size: DEFAULT_LOCAL_PAGE_SIZE,
  total: 0,
  items: [],
}

const formatOtaDisplayText = (value: string): string =>
  value
    .replace(/(^|[^.])mender\s+server/gi, (_match, prefix: string) => `${prefix}OTA 服务`)
    .replace(/(^|[^.])mender/gi, (_match, prefix: string) => `${prefix}OTA`)

const getLastPage = (total: number, pageSize: number) =>
  Math.max(1, Math.ceil(total / pageSize))

const statusColor = (status?: string | null) => {
  if (!status) return 'default'
  if (['success', 'uploaded', 'built', 'bound', 'resolved'].includes(status)) return 'green'
  if (['failed', 'sync_failed', 'error', 'conflict', 'open'].includes(status)) return 'red'
  if (['running', 'building', 'pending', 'verifying', 'canceling'].includes(status)) return 'blue'
  if (['disabled', 'canceled', 'ignored'].includes(status)) return 'default'
  return 'gold'
}

const getDeviceUpgradeStatusLabel = (
  t: TranslationFunction,
  status?: string | null,
) => {
  if (!status) return t('admin:deviceUpgrade.statuses.none', { defaultValue: '无状态' })
  if (status === 'unbound') return '未绑定'

  return t(`admin:deviceUpgrade.statuses.${status}`, {
    defaultValue: t('admin:deviceUpgrade.statuses.unknown', { defaultValue: '未知状态' }),
  })
}

const getBatchDeviceDisplayStatus = (
  batchStatus?: string | null,
  deviceStatus?: string | null,
) => {
  const normalizedDeviceStatus = deviceStatus?.trim()
  if (!normalizedDeviceStatus) return deviceStatus
  if (!CANCELABLE_BATCH_DEVICE_STATUSES.has(normalizedDeviceStatus)) {
    return normalizedDeviceStatus
  }
  if (batchStatus === 'canceling') return 'canceling'
  if (batchStatus === 'canceled') return 'canceled'
  if (PENDING_BATCH_DEVICE_STATUSES.has(normalizedDeviceStatus)) return 'pending'
  if (RUNNING_BATCH_DEVICE_STATUSES.has(normalizedDeviceStatus)) return 'running'
  return normalizedDeviceStatus
}

const getBatchDeviceStatusOptions = (
  t: TranslationFunction,
  batchStatus?: string | null,
) => {
  const option = (status: string) => ({
    label: getDeviceUpgradeStatusLabel(t, status),
    value: status,
  })
  const baseOptions = [option('success'), option('failed')]

  if (batchStatus === 'canceling') {
    return [...baseOptions, option('canceling'), option('canceled')]
  }
  if (batchStatus === 'canceled') {
    return [...baseOptions, option('canceled')]
  }

  return [
    ...baseOptions,
    option('running'),
    option('verifying'),
    option('pending'),
    option('canceled'),
  ]
}

const includesKeyword = (keyword: string, values: Array<string | number | null | undefined>) => {
  const normalizedKeyword = normalizeSearchText(keyword).toLowerCase()
  if (!normalizedKeyword) return true

  return values.some((value) =>
    String(value ?? '').toLowerCase().includes(normalizedKeyword),
  )
}

const matchesBatchKeyword = (keyword: string, batch: DeviceUpgradeBatch) => {
  if (normalizeSearchText(keyword) === '-') {
    const description = batch.description
    return description === null ||
      description === undefined ||
      description === '' ||
      normalizeSearchText(description) === '-'
  }

  return includesKeyword(keyword, [
    batch.name,
    batch.target_version,
    batch.description,
  ])
}

const getLabeledUniqueOptions = <T,>(
  items: T[],
  getValue: (item: T) => string | null | undefined,
  getLabel: (value: string) => string,
) =>
  Array.from(new Set(items.map(getValue).filter(Boolean) as string[])).map((value) => ({
    label: getLabel(value),
    value,
  }))

const renderBindingProject = (record: { project_name?: string | null; project_id?: number | null }) =>
  record.project_name || record.project_id || '-'

const getBindingDeviceStatusValue = (status?: number | null) => {
  if (status === 1) return 'online'
  if (status === 0) return 'offline'
  return 'unknown'
}

const getBindingDeviceStatusLabel = (status?: number | null) => {
  const statusValue = getBindingDeviceStatusValue(status)
  if (statusValue === 'online') return '在线'
  if (statusValue === 'offline') return '离线'
  return '未知'
}

const getBindingDeviceStatusColor = (status?: number | null) => {
  const statusValue = getBindingDeviceStatusValue(status)
  if (statusValue === 'online') return 'green'
  if (statusValue === 'offline') return 'default'
  return 'gold'
}

const DeviceUpgradeManagement: React.FC<DeviceUpgradeManagementProps> = ({ section = 'bindings' }) => {
  const { message: rawMessage } = App.useApp()
  const message = useDedupedMessage(rawMessage)
  const { t } = useTranslation(['navigation', 'admin'])
  const formatApiError = useCallback(
    (error: unknown, fallback: string) => formatOtaDisplayText(getLocalizedApiErrorMessage(error, fallback, t)),
    [t],
  )
  const {
    canReadDeviceUpgradeBindingDetails,
    canReadDeviceUpgradeArtifacts,
    canReadDeviceUpgradeBatchDetails,
    canSyncDeviceUpgradeDevices,
    canCreateDeviceUpgradeArtifact,
    canUpdateDeviceUpgradeArtifact,
    canEnableDeviceUpgradeArtifact,
    canDisableDeviceUpgradeArtifact,
    canDeleteDeviceUpgradeArtifact,
    canCreateDeviceUpgradeBatch,
    canSyncDeviceUpgradeBatch,
    canCancelDeviceUpgradeBatch,
  } = usePermissions()
  const [loading, setLoading] = useState(false)
  const [artifacts, setArtifacts] = useState<DeviceUpgradeArtifact[]>([])
  const [bindings, setBindings] = useState<DeviceBinding[]>([])
  const [batches, setBatches] = useState<DeviceUpgradeBatch[]>([])
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false)
  const [artifactPage, setArtifactPage] = useState(1)
  const [artifactPageSize, setArtifactPageSize] = useState(DEFAULT_LOCAL_PAGE_SIZE)
  const [bindingPage, setBindingPage] = useState(1)
  const [bindingPageSize, setBindingPageSize] = useState(DEFAULT_LOCAL_PAGE_SIZE)
  const [batchPage, setBatchPage] = useState(1)
  const [batchPageSize, setBatchPageSize] = useState(DEFAULT_LOCAL_PAGE_SIZE)
  const [artifactModalOpen, setArtifactModalOpen] = useState(false)
  const [editArtifactTarget, setEditArtifactTarget] = useState<DeviceUpgradeArtifact | null>(null)
  const [disableArtifactTarget, setDisableArtifactTarget] = useState<DeviceUpgradeArtifact | null>(null)
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [batchArtifactsLoading, setBatchArtifactsLoading] = useState(false)
  const [batchSubmitting, setBatchSubmitting] = useState(false)
  const batchSubmittingRef = useRef(false)
  const [batchConflict, setBatchConflict] = useState<DeviceUpgradeBatchConflictDetail | null>(null)
  const [batchConflictPromptOpen, setBatchConflictPromptOpen] = useState(false)
  const [batchConflictDetailOpen, setBatchConflictDetailOpen] = useState(false)
  const [batchConflictKeyword, setBatchConflictKeyword] = useState('')
  const [batchConflictPage, setBatchConflictPage] = useState(1)
  const [artifactFile, setArtifactFile] = useState<File | null>(null)
  const [artifactMetadata, setArtifactMetadata] = useState<DeviceUpgradeArtifactMetadata | null>(null)
  const [artifactInspecting, setArtifactInspecting] = useState(false)
  const [bindingDeviceNameFilter, setBindingDeviceNameFilter] = useState('')
  const [bindingProjectFilter, setBindingProjectFilter] =
    useState<SelectFilterValue>(DEFAULT_BINDING_FILTERS.projectId)
  const [bindingDeviceStatusFilter, setBindingDeviceStatusFilter] =
    useState<SelectFilterValue>(DEFAULT_BINDING_FILTERS.deviceStatus)
  const [bindingStatusFilter, setBindingStatusFilter] =
    useState<SelectFilterValue>(DEFAULT_BINDING_FILTERS.bindingStatus)
  const [appliedBindingFilters, setAppliedBindingFilters] =
    useState<BindingFilters>(DEFAULT_BINDING_FILTERS)
  const appliedBindingFiltersRef = useRef<BindingFilters>(DEFAULT_BINDING_FILTERS)
  const [bindingImageUrlMap, setBindingImageUrlMap] = useState<Map<string, string>>(() => new Map())
  const [bindingDetailOpen, setBindingDetailOpen] = useState(false)
  const [selectedBindingDetail, setSelectedBindingDetail] = useState<BindingListRow | null>(null)
  const [artifactKeyword, setArtifactKeyword] = useState('')
  const [artifactVersionFilter, setArtifactVersionFilter] = useState('')
  const [artifactStatusFilter, setArtifactStatusFilter] =
    useState<SelectFilterValue>(DEFAULT_ARTIFACT_FILTERS.status)
  const [appliedArtifactFilters, setAppliedArtifactFilters] =
    useState<ArtifactFilters>(DEFAULT_ARTIFACT_FILTERS)
  const [batchKeyword, setBatchKeyword] = useState('')
  const [batchStatusFilter, setBatchStatusFilter] = useState<SelectFilterValue>()
  const [appliedBatchFilters, setAppliedBatchFilters] =
    useState<BatchFilters>(DEFAULT_BATCH_FILTERS)
  const [batchDetailOpen, setBatchDetailOpen] = useState(false)
  const [selectedBatchDetail, setSelectedBatchDetail] = useState<DeviceUpgradeBatch | null>(null)
  const [batchDeviceLoading, setBatchDeviceLoading] = useState(false)
  const [batchDeviceKeyword, setBatchDeviceKeyword] = useState('')
  const [batchDeviceStatusFilter, setBatchDeviceStatusFilter] = useState<SelectFilterValue>()
  const [appliedBatchDeviceFilters, setAppliedBatchDeviceFilters] =
    useState<BatchDeviceFilters>(DEFAULT_BATCH_DEVICE_FILTERS)
  const [batchDevicePage, setBatchDevicePage] = useState(1)
  const [batchDevicePageSize, setBatchDevicePageSize] = useState(DEFAULT_LOCAL_PAGE_SIZE)
  const [batchDeviceData, setBatchDeviceData] =
    useState<DeviceUpgradeBatchDevicePage>(DEFAULT_BATCH_DEVICE_PAGE)
  const [artifactForm] = Form.useForm<ArtifactFormValues>()
  const [editArtifactForm] = Form.useForm<EditArtifactFormValues>()
  const [disableArtifactForm] = Form.useForm<DisableArtifactFormValues>()
  const [batchForm] = Form.useForm<BatchFormValues>()
  const selectedBatchArtifactId = Form.useWatch('artifact_id', batchForm)
  const [devicePickerLoading, setDevicePickerLoading] = useState(false)
  const [devicePickerKeyword, setDevicePickerKeyword] = useState(DEFAULT_DEVICE_PICKER_FILTERS.keyword)
  const [devicePickerProjectIds, setDevicePickerProjectIds] =
    useState<number[]>(DEFAULT_DEVICE_PICKER_FILTERS.projectIds)
  const [appliedDevicePickerFilters, setAppliedDevicePickerFilters] =
    useState<DevicePickerFilters>(DEFAULT_DEVICE_PICKER_FILTERS)
  const [devicePickerQueryTrigger, setDevicePickerQueryTrigger] = useState(0)
  const [devicePickerPage, setDevicePickerPage] = useState(1)
  const [devicePickerPageSize, setDevicePickerPageSize] = useState(DEFAULT_LOCAL_PAGE_SIZE)
  const [selectableDevicePage, setSelectableDevicePage] = useState<SelectableUpgradeDevicePage>({
    page: 1,
    page_size: DEFAULT_LOCAL_PAGE_SIZE,
    total: 0,
    items: [],
  })
  const [selectedUpgradeDevices, setSelectedUpgradeDevices] = useState<Record<string, SelectableUpgradeDevice>>({})
  const [projectOptions, setProjectOptions] = useState<ProjectLookupItem[]>([])
  const [manualDeviceSyncBusy, setManualDeviceSyncBusy] = useState(false)
  const [batchSyncBusyById, setBatchSyncBusyById] = useState<Record<number, boolean>>({})
  const lastSelectableDeviceSyncAtRef = useRef(0)
  const manualDeviceSyncBusyRef = useRef(false)
  const manualDeviceSyncBusyTimerRef = useRef<number | null>(null)
  const batchSyncBusyIdsRef = useRef<Set<number>>(new Set())
  const batchSyncBusyTimerRefs = useRef<Map<number, number>>(new Map())
  const batchArtifactRequestIdRef = useRef(0)

  useEffect(() => () => {
    if (manualDeviceSyncBusyTimerRef.current !== null) {
      window.clearTimeout(manualDeviceSyncBusyTimerRef.current)
    }
    batchSyncBusyTimerRefs.current.forEach((timerId) => {
      window.clearTimeout(timerId)
    })
  }, [])

  const loadData = useCallback(async (bindingFilters = appliedBindingFiltersRef.current) => {
    setLoading(true)
    try {
      const shouldLoadArtifacts = section === 'artifacts'
      const shouldLoadBindings = section === 'bindings'
      const shouldLoadBatches = section === 'batches'
      const [artifactData, bindingData, batchData] =
        await Promise.all([
          shouldLoadArtifacts ? getDeviceUpgradeArtifacts() : Promise.resolve([]),
          shouldLoadBindings
            ? getDeviceBindings({
                keyword: bindingFilters.deviceName,
                project_id: bindingFilters.projectId,
                device_status: bindingFilters.deviceStatus,
                binding_status: bindingFilters.bindingStatus,
              })
            : Promise.resolve([]),
          shouldLoadBatches ? getDeviceUpgradeBatches() : Promise.resolve([]),
        ])
      setArtifacts(artifactData)
      setBindings(bindingData)
      setBatches(batchData)
      setHasLoadedInitialData(true)
    } catch (error) {
      console.error(error)
      message.error('加载设备升级数据失败')
    } finally {
      setLoading(false)
    }
  }, [
    message,
    section,
  ])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const selectedDeviceIds = useMemo(
    () => Object.keys(selectedUpgradeDevices),
    [selectedUpgradeDevices],
  )

  const selectedArtifactId = useMemo(() => {
    const normalized = Number(selectedBatchArtifactId)
    return Number.isFinite(normalized) && normalized > 0 ? normalized : undefined
  }, [selectedBatchArtifactId])

  const clearBatchConflict = useCallback(() => {
    setBatchConflict(null)
    setBatchConflictPromptOpen(false)
    setBatchConflictDetailOpen(false)
    setBatchConflictKeyword('')
    setBatchConflictPage(1)
  }, [])

  useEffect(() => {
    clearBatchConflict()
  }, [clearBatchConflict, selectedArtifactId])

  const ensureSelectableDevicesSynced = useCallback(async () => {
    if (!canSyncDeviceUpgradeDevices) {
      return
    }
    const now = Date.now()
    if (now - lastSelectableDeviceSyncAtRef.current < SELECTABLE_DEVICE_SYNC_MIN_INTERVAL_MS) {
      return
    }
    lastSelectableDeviceSyncAtRef.current = now
    try {
      await syncMenderDevices()
    } catch (error) {
      console.error(error)
      message.warning(formatOtaDisplayText(formatApiError(error, '自动同步 OTA 设备失败，设备列表可能不是最新')))
    }
  }, [canSyncDeviceUpgradeDevices, formatApiError, message])

  const loadSelectableDevices = useCallback(async (syncBeforeLoad = true) => {
    if (!batchModalOpen || !selectedArtifactId || !canCreateDeviceUpgradeBatch) {
      setSelectableDevicePage({
        page: 1,
        page_size: devicePickerPageSize,
        total: 0,
        items: [],
      })
      return
    }

    setDevicePickerLoading(true)
    try {
      if (syncBeforeLoad) {
        await ensureSelectableDevicesSynced()
      }
      const pageData = await getSelectableUpgradeDevices({
        artifact_id: selectedArtifactId,
        page: devicePickerPage,
        page_size: devicePickerPageSize,
        keyword: appliedDevicePickerFilters.keyword,
        project_ids: appliedDevicePickerFilters.projectIds,
      })
      setSelectableDevicePage(pageData)
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '加载可升级设备失败'))
      setSelectableDevicePage({
        page: devicePickerPage,
        page_size: devicePickerPageSize,
        total: 0,
        items: [],
      })
    } finally {
      setDevicePickerLoading(false)
    }
  }, [
    batchModalOpen,
    appliedDevicePickerFilters,
    canCreateDeviceUpgradeBatch,
    devicePickerPage,
    devicePickerPageSize,
    ensureSelectableDevicesSynced,
    formatApiError,
    message,
    selectedArtifactId,
  ])

  useEffect(() => {
    if (!batchModalOpen) return
    void loadSelectableDevices()
  }, [batchModalOpen, devicePickerQueryTrigger, loadSelectableDevices])

  const loadBatchDevices = useCallback(async () => {
    if (!batchDetailOpen || !selectedBatchDetail || !canReadDeviceUpgradeBatchDetails) {
      setBatchDeviceData({
        page: 1,
        page_size: batchDevicePageSize,
        total: 0,
        items: [],
      })
      return
    }

    setBatchDeviceLoading(true)
    try {
      const pageData = await getDeviceUpgradeBatchDevices(selectedBatchDetail.id, {
        page: batchDevicePage,
        page_size: batchDevicePageSize,
        keyword: appliedBatchDeviceFilters.keyword,
        status: appliedBatchDeviceFilters.status,
      })
      setBatchDeviceData(pageData)
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '加载升级批次设备明细失败'))
      setBatchDeviceData({
        page: batchDevicePage,
        page_size: batchDevicePageSize,
        total: 0,
        items: [],
      })
    } finally {
      setBatchDeviceLoading(false)
    }
  }, [
    batchDetailOpen,
    appliedBatchDeviceFilters,
    batchDevicePage,
    batchDevicePageSize,
    canReadDeviceUpgradeBatchDetails,
    formatApiError,
    message,
    selectedBatchDetail,
  ])

  useEffect(() => {
    if (!batchDetailOpen) return
    void loadBatchDevices()
  }, [batchDetailOpen, loadBatchDevices])

  const upsertBatchRow = useCallback((batch: DeviceUpgradeBatch) => {
    setBatches((current) =>
      current.map((item) => (item.id === batch.id ? batch : item)),
    )
    setSelectedBatchDetail((current) =>
      current?.id === batch.id ? batch : current,
    )
  }, [])

  useEffect(() => {
    if (!batchModalOpen) return
    setDevicePickerKeyword(DEFAULT_DEVICE_PICKER_FILTERS.keyword)
    setDevicePickerProjectIds(DEFAULT_DEVICE_PICKER_FILTERS.projectIds)
    setDevicePickerPage(1)
    setSelectedUpgradeDevices({})
    setAppliedDevicePickerFilters(DEFAULT_DEVICE_PICKER_FILTERS)
    setDevicePickerQueryTrigger((current) => current + 1)
  }, [batchModalOpen, selectedArtifactId])

  useEffect(() => {
    let canceled = false
    lookupProjects({ includeDisabled: true })
      .then((projects) => {
        if (!canceled) setProjectOptions(projects)
      })
      .catch(() => {
        if (!canceled) setProjectOptions([])
      })

    return () => {
      canceled = true
    }
  }, [])

  const bindingRows = useMemo<BindingListRow[]>(
    () => bindings.map((binding) => ({
      id: binding.id,
      rowKey: `device-${binding.id}`,
      type: binding.type,
      name: binding.name,
      status: binding.status,
      installation_image_path: binding.installation_image_path,
      current_device_version: binding.current_device_version,
      package_key: binding.package_key,
      mender_device_id: binding.mender_device_id,
      mender_hardware_id: binding.mender_hardware_id,
      mender_physical_address: binding.mender_physical_address,
      mender_device_type: binding.mender_device_type,
      mender_device_system: binding.mender_device_system,
      mender_last_seen_at: binding.mender_last_seen_at,
      mender_display_seen_at: binding.mender_display_seen_at,
      mender_probe_status: binding.mender_probe_status,
      mender_probe_checked_at: binding.mender_probe_checked_at,
      mender_probe_error: binding.mender_probe_error,
      ota_binding_status: binding.ota_binding_status,
      ota_current_app_version: binding.ota_current_app_version,
      ota_current_model_version: binding.ota_current_model_version,
      ota_last_inventory_sync_at: binding.ota_last_inventory_sync_at,
      ota_version_status: binding.ota_version_status,
      ota_version_observed_at: binding.ota_version_observed_at,
      project_id: binding.project_id,
      project_name: binding.project_name,
    })),
    [bindings],
  )

  useEffect(() => {
    const hydrateImageUrls = async () => {
      try {
        const urlMap = await resolveManagedObjectUrls(
          bindingRows.map((item) => item.installation_image_path),
        )
        setBindingImageUrlMap(urlMap)
      } catch {
        setBindingImageUrlMap(new Map())
      }
    }

    void hydrateImageUrls()
  }, [bindingRows])

  const filteredBindingRows = useMemo(
    () =>
      bindingRows.filter((item) =>
        includesKeyword(appliedBindingFilters.deviceName, [item.name]) &&
        (!appliedBindingFilters.projectId ||
          appliedBindingFilters.projectId === 'all' ||
          String(item.project_id ?? '') === appliedBindingFilters.projectId) &&
        (!appliedBindingFilters.deviceStatus ||
          appliedBindingFilters.deviceStatus === 'all' ||
          getBindingDeviceStatusValue(item.status) === appliedBindingFilters.deviceStatus) &&
        (!appliedBindingFilters.bindingStatus ||
          appliedBindingFilters.bindingStatus === 'all' ||
          item.ota_binding_status === appliedBindingFilters.bindingStatus),
      ),
    [appliedBindingFilters, bindingRows],
  )

  const filteredArtifacts = useMemo(
    () =>
      artifacts.filter((item) =>
        includesKeyword(appliedArtifactFilters.keyword, [item.name]) &&
        includesKeyword(appliedArtifactFilters.version, [item.version]) &&
        (!appliedArtifactFilters.status ||
          appliedArtifactFilters.status === 'all' ||
          (appliedArtifactFilters.status === 'disabled'
            ? item.status === 'disabled'
            : item.status !== 'disabled')),
      ),
    [appliedArtifactFilters, artifacts],
  )

  const filteredBatches = useMemo(
    () =>
      batches.filter((item) =>
        matchesBatchKeyword(appliedBatchFilters.keyword, item) &&
        (!appliedBatchFilters.status || item.status === appliedBatchFilters.status),
      ),
    [appliedBatchFilters, batches],
  )

  const currentPageActiveBatchIds = useMemo(
    () =>
      filteredBatches
        .slice((batchPage - 1) * batchPageSize, batchPage * batchPageSize)
        .filter((batch) => AUTO_REFRESH_BATCH_STATUSES.has(batch.status))
        .map((batch) => batch.id),
    [batchPage, batchPageSize, filteredBatches],
  )

  useEffect(() => {
    if (
      section !== 'batches' ||
      !canReadDeviceUpgradeBatchDetails ||
      currentPageActiveBatchIds.length === 0
    ) {
      return undefined
    }

    let canceled = false
    const refreshActiveBatchRows = async () => {
      await Promise.all(
        currentPageActiveBatchIds.map(async (batchId) => {
          try {
            const batch = await getDeviceUpgradeBatch(batchId)
            if (!canceled) {
              upsertBatchRow(batch)
            }
          } catch (error) {
            console.error(error)
          }
        }),
      )
    }

    const timer = window.setInterval(() => {
      void refreshActiveBatchRows()
    }, BATCH_ROW_AUTO_REFRESH_INTERVAL_MS)

    return () => {
      canceled = true
      window.clearInterval(timer)
    }
  }, [canReadDeviceUpgradeBatchDetails, currentPageActiveBatchIds, section, upsertBatchRow])

  useEffect(() => {
    setArtifactPage((current) => Math.min(current, getLastPage(filteredArtifacts.length, artifactPageSize)))
  }, [artifactPageSize, filteredArtifacts.length])

  useEffect(() => {
    setBindingPage((current) => Math.min(current, getLastPage(filteredBindingRows.length, bindingPageSize)))
  }, [bindingPageSize, filteredBindingRows.length])

  useEffect(() => {
    setBatchPage((current) => Math.min(current, getLastPage(filteredBatches.length, batchPageSize)))
  }, [batchPageSize, filteredBatches.length])

  const handleLocalPaginationChange = useCallback((
    page: number,
    nextPageSize: number | undefined,
    currentPageSize: number,
    setPage: PaginationStateSetter,
    setPageSize: PaginationStateSetter,
  ) => {
    const resolvedPageSize = nextPageSize ?? currentPageSize
    setPageSize(resolvedPageSize)
    setPage(resolvedPageSize !== currentPageSize ? 1 : page)
  }, [])

  const artifactPagination = useMemo(
    () =>
      buildPaginatedListPagination({
        current: artifactPage,
        pageSize: artifactPageSize,
        total: filteredArtifacts.length,
        showTotal: (total) =>
          t('admin:deviceUpgrade.pagination.totalArtifacts', {
            total,
            defaultValue: '共 {{total}} 个升级包',
          }),
        onChange: (page, pageSize) =>
          handleLocalPaginationChange(
            page,
            pageSize,
            artifactPageSize,
            setArtifactPage,
            setArtifactPageSize,
          ),
      }),
    [artifactPage, artifactPageSize, filteredArtifacts.length, handleLocalPaginationChange, t],
  )

  const bindingPagination = useMemo(
    () =>
      buildPaginatedListPagination({
        current: bindingPage,
        pageSize: bindingPageSize,
        total: filteredBindingRows.length,
        showTotal: (total) =>
          t('admin:deviceUpgrade.pagination.totalBindings', {
            total,
            defaultValue: '共 {{total}} 条绑定记录',
          }),
        onChange: (page, pageSize) =>
          handleLocalPaginationChange(
            page,
            pageSize,
            bindingPageSize,
            setBindingPage,
            setBindingPageSize,
          ),
      }),
    [bindingPage, bindingPageSize, filteredBindingRows.length, handleLocalPaginationChange, t],
  )

  const batchPagination = useMemo(
    () =>
      buildPaginatedListPagination({
        current: batchPage,
        pageSize: batchPageSize,
        total: filteredBatches.length,
        showTotal: (total) =>
          t('admin:deviceUpgrade.pagination.totalBatches', {
            total,
            defaultValue: '共 {{total}} 个批次',
          }),
        onChange: (page, pageSize) =>
          handleLocalPaginationChange(
            page,
            pageSize,
            batchPageSize,
            setBatchPage,
            setBatchPageSize,
          ),
      }),
    [batchPage, batchPageSize, filteredBatches.length, handleLocalPaginationChange, t],
  )

  const batchDevicePagination = useMemo(
    () =>
      buildPaginatedListPagination({
        current: batchDevicePage,
        pageSize: batchDevicePageSize,
        total: batchDeviceData.total,
        showTotal: (total) => `共 ${total} 台批次设备`,
        onChange: (page, pageSize) =>
          handleLocalPaginationChange(
            page,
            pageSize,
            batchDevicePageSize,
            setBatchDevicePage,
            setBatchDevicePageSize,
          ),
      }),
    [
      batchDeviceData.total,
      batchDevicePage,
      batchDevicePageSize,
      handleLocalPaginationChange,
    ],
  )

  const bindingStatusOptions = useMemo(
    () => [
      { label: '全部绑定状态', value: 'all' },
      { label: getDeviceUpgradeStatusLabel(t, 'bound'), value: 'bound' },
      { label: getDeviceUpgradeStatusLabel(t, 'unbound'), value: 'unbound' },
    ],
    [t],
  )
  const bindingProjectOptions = useMemo(
    () => [
      { label: '全部项目', value: 'all' },
      ...projectOptions.map((project) => ({
        label: project.name,
        value: String(project.id),
      })),
    ],
    [projectOptions],
  )
  const bindingDeviceStatusOptions = useMemo(
    () => [
      { label: '全部状态', value: 'all' },
      { label: '在线', value: 'online' },
      { label: '离线', value: 'offline' },
    ],
    [],
  )
  const artifactStatusOptions = useMemo(
    () => [
      { label: '全部状态', value: 'all' },
      {
        label: t('admin:deviceUpgrade.statuses.enabled', { defaultValue: '启用' }),
        value: 'enabled',
      },
      {
        label: t('admin:deviceUpgrade.statuses.disabled', { defaultValue: '停用' }),
        value: 'disabled',
      },
    ],
    [t],
  )
  const batchStatusOptions = useMemo(
    () =>
      getLabeledUniqueOptions(batches, (item) => item.status, (status) =>
        getDeviceUpgradeStatusLabel(t, status),
      ),
    [batches, t],
  )
  const batchDeviceStatusOptions = useMemo(
    () => getBatchDeviceStatusOptions(t, selectedBatchDetail?.status),
    [selectedBatchDetail?.status, t],
  )
  const applyBindingFilters = () => {
    const nextFilters = {
      deviceName: normalizeSearchText(bindingDeviceNameFilter),
      projectId: bindingProjectFilter,
      deviceStatus: bindingDeviceStatusFilter,
      bindingStatus: bindingStatusFilter,
    }
    appliedBindingFiltersRef.current = nextFilters
    setAppliedBindingFilters(nextFilters)
    setBindingPage(1)
    void loadData(nextFilters)
  }

  const applyArtifactFilters = () => {
    setAppliedArtifactFilters({
      keyword: normalizeSearchText(artifactKeyword),
      version: normalizeSearchText(artifactVersionFilter),
      status: artifactStatusFilter,
    })
    setArtifactPage(1)
    void loadData()
  }

  const applyBatchFilters = () => {
    setAppliedBatchFilters({
      keyword: normalizeSearchText(batchKeyword),
      status: batchStatusFilter,
    })
    setBatchPage(1)
    void loadData()
  }

  const resetBindingFilters = () => {
    setBindingDeviceNameFilter(DEFAULT_BINDING_FILTERS.deviceName)
    setBindingProjectFilter(DEFAULT_BINDING_FILTERS.projectId)
    setBindingDeviceStatusFilter(DEFAULT_BINDING_FILTERS.deviceStatus)
    setBindingStatusFilter(DEFAULT_BINDING_FILTERS.bindingStatus)
    appliedBindingFiltersRef.current = DEFAULT_BINDING_FILTERS
    setAppliedBindingFilters(DEFAULT_BINDING_FILTERS)
    setBindingPage(1)
    void loadData(DEFAULT_BINDING_FILTERS)
  }

  const resetArtifactFilters = () => {
    setArtifactKeyword(DEFAULT_ARTIFACT_FILTERS.keyword)
    setArtifactVersionFilter(DEFAULT_ARTIFACT_FILTERS.version)
    setArtifactStatusFilter(DEFAULT_ARTIFACT_FILTERS.status)
    setAppliedArtifactFilters(DEFAULT_ARTIFACT_FILTERS)
    setArtifactPage(1)
  }

  const resetBatchFilters = () => {
    setBatchKeyword(DEFAULT_BATCH_FILTERS.keyword)
    setBatchStatusFilter(DEFAULT_BATCH_FILTERS.status)
    setAppliedBatchFilters(DEFAULT_BATCH_FILTERS)
    setBatchPage(1)
  }

  const applyBatchDeviceFilters = () => {
    setAppliedBatchDeviceFilters({
      keyword: normalizeSearchText(batchDeviceKeyword),
      status: batchDeviceStatusFilter,
    })
    setBatchDevicePage(1)
  }

  const resetBatchDeviceFilters = () => {
    setBatchDeviceKeyword(DEFAULT_BATCH_DEVICE_FILTERS.keyword)
    setBatchDeviceStatusFilter(DEFAULT_BATCH_DEVICE_FILTERS.status)
    setAppliedBatchDeviceFilters(DEFAULT_BATCH_DEVICE_FILTERS)
    setBatchDevicePage(1)
    setBatchDevicePageSize(DEFAULT_LOCAL_PAGE_SIZE)
  }

  const applyDevicePickerFilters = () => {
    setAppliedDevicePickerFilters({
      keyword: normalizeSearchText(devicePickerKeyword),
      projectIds: devicePickerProjectIds,
    })
    setDevicePickerPage(1)
    setDevicePickerQueryTrigger((current) => current + 1)
  }

  const resetDevicePickerFilters = () => {
    setDevicePickerKeyword(DEFAULT_DEVICE_PICKER_FILTERS.keyword)
    setDevicePickerProjectIds(DEFAULT_DEVICE_PICKER_FILTERS.projectIds)
    setAppliedDevicePickerFilters(DEFAULT_DEVICE_PICKER_FILTERS)
    setDevicePickerPage(1)
    setDevicePickerPageSize(DEFAULT_LOCAL_PAGE_SIZE)
    setDevicePickerQueryTrigger((current) => current + 1)
  }

  const resetDevicePickerState = () => {
    resetDevicePickerFilters()
    setSelectedUpgradeDevices({})
    setSelectableDevicePage({
      page: 1,
      page_size: DEFAULT_LOCAL_PAGE_SIZE,
      total: 0,
      items: [],
    })
    clearBatchConflict()
  }

  const closeBatchModal = () => {
    batchArtifactRequestIdRef.current += 1
    setBatchModalOpen(false)
    setBatchArtifactsLoading(false)
    batchSubmittingRef.current = false
    setBatchSubmitting(false)
    batchForm.resetFields()
    resetDevicePickerState()
  }

  const openBatchModal = async () => {
    if (!canCreateDeviceUpgradeBatch) return
    batchForm.resetFields()
    setArtifacts([])
    resetDevicePickerState()
    setBatchModalOpen(true)
    if (!canReadDeviceUpgradeArtifacts) return

    const requestId = batchArtifactRequestIdRef.current + 1
    batchArtifactRequestIdRef.current = requestId
    const isCurrentRequest = () => batchArtifactRequestIdRef.current === requestId
    setBatchArtifactsLoading(true)
    try {
      const artifactData = await getDeviceUpgradeArtifacts()
      if (isCurrentRequest()) {
        setArtifacts(artifactData)
      }
    } catch (error) {
      console.error(error)
      if (isCurrentRequest()) {
        setArtifacts([])
        message.error(formatApiError(error, '获取升级包信息失败'))
      }
    } finally {
      if (isCurrentRequest()) {
        setBatchArtifactsLoading(false)
      }
    }
  }

  const closeBatchDetail = () => {
    setBatchDetailOpen(false)
    setSelectedBatchDetail(null)
    resetBatchDeviceFilters()
    setBatchDeviceData(DEFAULT_BATCH_DEVICE_PAGE)
  }

  const openBatchDetail = (batch: DeviceUpgradeBatch) => {
    if (!canReadDeviceUpgradeBatchDetails) return
    resetBatchDeviceFilters()
    setSelectedBatchDetail(batch)
    setBatchDetailOpen(true)
  }

  const closeBindingDetail = () => {
    setBindingDetailOpen(false)
    setSelectedBindingDetail(null)
  }

  const openBindingDetail = (binding: BindingListRow) => {
    if (!canReadDeviceUpgradeBindingDetails) return
    setSelectedBindingDetail(binding)
    setBindingDetailOpen(true)
  }

  const resetArtifactModalState = () => {
    setArtifactModalOpen(false)
    setArtifactFile(null)
    setArtifactMetadata(null)
    setArtifactInspecting(false)
    artifactForm.resetFields()
  }

  const openEditArtifactModal = (artifact: DeviceUpgradeArtifact) => {
    setEditArtifactTarget(artifact)
    editArtifactForm.setFieldsValue({
      description: artifact.description || '',
    })
  }

  const closeEditArtifactModal = () => {
    setEditArtifactTarget(null)
    editArtifactForm.resetFields()
  }

  const openDisableArtifactModal = (artifact: DeviceUpgradeArtifact) => {
    setDisableArtifactTarget(artifact)
    disableArtifactForm.setFieldsValue({
      description: artifact.description || '',
    })
  }

  const closeDisableArtifactModal = () => {
    setDisableArtifactTarget(null)
    disableArtifactForm.resetFields()
  }

  const inspectArtifactFile = async (file: File) => {
    if (!canCreateDeviceUpgradeArtifact) return
    setArtifactFile(file)
    setArtifactMetadata(null)
    setArtifactInspecting(true)
    try {
      const metadata = await inspectDeviceUpgradeArtifact(file)
      setArtifactMetadata(metadata)
      artifactForm.setFieldsValue({
        name: metadata.suggested_name,
        package_type: metadata.package_type,
        version: metadata.software_version,
        device_type: metadata.device_type,
        artifact_name: metadata.artifact_name,
      })
      message.success('已读取升级包信息')
    } catch (error) {
      console.error(error)
      setArtifactFile(null)
      artifactForm.setFieldsValue({
        name: undefined,
        package_type: undefined,
        version: undefined,
        device_type: undefined,
        artifact_name: undefined,
      })
      message.error(formatApiError(error, '读取升级包信息失败'))
    } finally {
      setArtifactInspecting(false)
    }
  }

  const handleArtifactBeforeUpload = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.mender')) {
      setArtifactFile(null)
      setArtifactMetadata(null)
      artifactForm.setFieldsValue({
        name: undefined,
        package_type: undefined,
        version: undefined,
        device_type: undefined,
        artifact_name: undefined,
      })
      message.error('请选择 .mender 文件')
      return Upload.LIST_IGNORE
    }
    void inspectArtifactFile(file)
    return false
  }

  const createArtifact = async () => {
    if (!canCreateDeviceUpgradeArtifact) return
    const values = await artifactForm.validateFields()
    if (!artifactFile || !artifactMetadata) {
      message.error('请先选择并成功读取 .mender 文件')
      return
    }
    try {
      await registerPrebuiltDeviceUpgradeArtifact({
        ...values,
        is_stable: false,
        artifact_file: artifactFile,
      })
      message.success('升级包已添加并上传到 OTA 服务')
      resetArtifactModalState()
      await loadData()
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '添加升级包失败'))
    }
  }

  const createBatch = async () => {
    if (!canCreateDeviceUpgradeBatch || batchSubmittingRef.current) return
    batchSubmittingRef.current = true
    setBatchSubmitting(true)
    try {
      const values = await batchForm.validateFields()
      if (selectedDeviceIds.length === 0) {
        message.error('请选择目标设备')
        return
      }
      clearBatchConflict()
      await createDeviceUpgradeBatch({
        ...values,
        device_ids: selectedDeviceIds,
      })
      message.success('升级批次已创建')
      closeBatchModal()
      await loadData()
    } catch (error) {
      console.error(error)
      const conflict = extractDeviceUpgradeBatchConflict(error)
      if (conflict) {
        setBatchConflict(conflict)
        setBatchConflictPromptOpen(true)
        return
      }
      message.error(formatApiError(error, '创建升级批次失败'))
    } finally {
      batchSubmittingRef.current = false
      setBatchSubmitting(false)
    }
  }

  const renderStatusTag = (status?: string | null) => (
    <Tag color={statusColor(status)} title={status || undefined}>
      {getDeviceUpgradeStatusLabel(t, status)}
    </Tag>
  )

  const renderBatchDeviceStatusTag = (status?: string | null) => {
    const displayStatus = getBatchDeviceDisplayStatus(selectedBatchDetail?.status, status)
    return (
      <Tag color={statusColor(displayStatus)} title={status || undefined}>
        {getDeviceUpgradeStatusLabel(t, displayStatus)}
      </Tag>
    )
  }

  const handleDisableArtifact = async () => {
    if (!canDisableDeviceUpgradeArtifact) return
    if (!disableArtifactTarget) return
    const values = await disableArtifactForm.validateFields()
    try {
      await disableDeviceUpgradeArtifact(disableArtifactTarget.id, {
        description: values.description.trim(),
      })
      message.success('升级包已停用')
      closeDisableArtifactModal()
      await loadData()
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '停用升级包失败'))
    }
  }

  const handleUpdateArtifactDescription = async () => {
    if (!canUpdateDeviceUpgradeArtifact) return
    if (!editArtifactTarget) return
    const values = await editArtifactForm.validateFields()
    const description = values.description?.trim() || null
    try {
      await updateDeviceUpgradeArtifactDescription(editArtifactTarget.id, {
        description,
      })
      message.success('升级包备注已更新')
      closeEditArtifactModal()
      await loadData()
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '更新升级包备注失败'))
    }
  }

  const handleEnableArtifact = async (artifactId: number) => {
    if (!canEnableDeviceUpgradeArtifact) return
    try {
      await enableDeviceUpgradeArtifact(artifactId)
      message.success('升级包已恢复使用')
      await loadData()
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '恢复升级包失败'))
    }
  }

  const handleDeleteArtifact = async (artifactId: number) => {
    if (!canDeleteDeviceUpgradeArtifact) return
    try {
      await deleteDeviceUpgradeArtifact(artifactId)
      message.success('升级包已删除')
      await loadData()
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '删除升级包失败'))
    }
  }

  const handleSyncMenderDevices = async () => {
    if (!canSyncDeviceUpgradeDevices) return
    if (manualDeviceSyncBusyRef.current) return
    const startedAt = Date.now()
    let syncFeedback: (() => void) | null = null
    if (manualDeviceSyncBusyTimerRef.current !== null) {
      window.clearTimeout(manualDeviceSyncBusyTimerRef.current)
      manualDeviceSyncBusyTimerRef.current = null
    }
    manualDeviceSyncBusyRef.current = true
    setManualDeviceSyncBusy(true)
    try {
      const result = await syncMenderDevices({ operationSource: 'manual' })
      lastSelectableDeviceSyncAtRef.current = Date.now()
      if (!result.skipped) {
        await loadData()
        syncFeedback = () => message.success('OTA 设备已同步')
      }
    } catch (error) {
      console.error(error)
      const errorMessage = formatOtaDisplayText(formatApiError(error, '同步 OTA 设备失败'))
      syncFeedback = () => message.error(errorMessage)
    } finally {
      const remainingMs = Math.max(0, MANUAL_DEVICE_SYNC_BUSY_MS - (Date.now() - startedAt))
      manualDeviceSyncBusyTimerRef.current = window.setTimeout(() => {
        manualDeviceSyncBusyRef.current = false
        flushSync(() => {
          setManualDeviceSyncBusy(false)
        })
        manualDeviceSyncBusyTimerRef.current = null
        syncFeedback?.()
      }, remainingMs)
    }
  }

  const handleSyncBatch = async (batchId: number) => {
    if (!canSyncDeviceUpgradeBatch) return
    if (batchSyncBusyIdsRef.current.has(batchId)) return
    const startedAt = Date.now()
    const existingTimer = batchSyncBusyTimerRefs.current.get(batchId)
    if (existingTimer !== undefined) {
      window.clearTimeout(existingTimer)
      batchSyncBusyTimerRefs.current.delete(batchId)
    }
    batchSyncBusyIdsRef.current.add(batchId)
    setBatchSyncBusyById((prev) => ({ ...prev, [batchId]: true }))
    let syncFeedback: (() => void) | null = null
    try {
      const batch = await syncDeviceUpgradeBatch(batchId)
      if (!batch.skipped) {
        upsertBatchRow(batch)
        syncFeedback = () => message.success('升级批次已同步')
      }
    } catch (error) {
      console.error(error)
      const errorMessage = formatApiError(error, '同步升级批次失败')
      syncFeedback = () => message.error(errorMessage)
    } finally {
      const remainingMs = Math.max(0, MANUAL_DEVICE_SYNC_BUSY_MS - (Date.now() - startedAt))
      const timerId = window.setTimeout(() => {
        batchSyncBusyIdsRef.current.delete(batchId)
        flushSync(() => {
          setBatchSyncBusyById((prev) => {
            const next = { ...prev }
            delete next[batchId]
            return next
          })
        })
        batchSyncBusyTimerRefs.current.delete(batchId)
        syncFeedback?.()
      }, remainingMs)
      batchSyncBusyTimerRefs.current.set(batchId, timerId)
    }
  }

  const handleCancelBatch = async (batchId: number) => {
    if (!canCancelDeviceUpgradeBatch) return
    try {
      const batch = await cancelDeviceUpgradeBatch(batchId)
      message.success(
        batch.status === 'canceled'
          ? t('admin:deviceUpgrade.messages.batchCanceled', { defaultValue: '升级批次已取消' })
          : t('admin:deviceUpgrade.messages.batchCancelRequested', {
            defaultValue: '取消请求已提交，正在确认设备状态',
          }),
      )
      upsertBatchRow(batch)
    } catch (error) {
      console.error(error)
      message.error(formatApiError(error, '取消升级批次失败'))
    }
  }

  const renderNullableText = (value?: string | number | null) =>
    value === undefined || value === null || value === '' ? '-' : value

  const renderOtaDisplayText = (value?: string | number | null) =>
    renderPaginatedListText(typeof value === 'string' ? formatOtaDisplayText(value) : value)

  const renderBatchDeviceFailureReason = (
    value: unknown,
    record: DeviceUpgradeBatchDevice,
  ) => {
    const displayStatus = getBatchDeviceDisplayStatus(selectedBatchDetail?.status, record.status)
    if (displayStatus !== 'failed') {
      return renderPaginatedListText(null)
    }
    return renderOtaDisplayText(typeof value === 'string' ? value : null)
  }

  const getOtaVersionStatusLabel = (status?: string | null) => {
    switch (status) {
      case 'fresh':
        return '已确认'
      case 'syncing':
      case 'stale':
      case 'unconfirmed':
        return '同步中'
      case 'unreported':
        return '未上报'
      default:
        return '未上报'
    }
  }

  const getOtaVersionStatusColor = (status?: string | null) => {
    switch (status) {
      case 'fresh':
        return 'green'
      case 'syncing':
      case 'stale':
      case 'unconfirmed':
        return 'orange'
      default:
        return 'default'
    }
  }

  const renderOtaAppVersion = (
    value?: string | null,
    status?: string | null,
  ) => {
    if (value) {
      return renderPaginatedListText(value)
    }
    const label = getOtaVersionStatusLabel(status)
    return (
      <Space size={4} wrap>
        <Tag color={getOtaVersionStatusColor(status)}>{label}</Tag>
      </Space>
    )
  }

  const renderBindingCurrentVersion = (
    value?: string | null,
    packageKey?: string | null,
  ) =>
    value ? (
      <Tag color="blue" data-testid="binding-current-version-tag">
        {getDevicePackageDisplayName(packageKey, value, t)}
      </Tag>
    ) : (
      <Typography.Text type="secondary">
        {t('admin:deviceUpgrade.empty.notSet', { defaultValue: '未设置' })}
      </Typography.Text>
    )

  const renderDateTime = (value?: string | null) => localeService.formatDateTime(value)

  const renderBindingDeviceStatusTag = (status?: number | null) => (
    <Tag color={getBindingDeviceStatusColor(status)}>
      {getBindingDeviceStatusLabel(status)}
    </Tag>
  )

  const getManagedImageUrl = (
    path: string | null | undefined,
    urlMap: Map<string, string>,
  ) => (path ? urlMap.get(path) || path : null)

  const getBindingImageUrl = (path?: string | null) =>
    getManagedImageUrl(path, bindingImageUrlMap)

  const renderBindingImage = (path?: string | null) => {
    const imageUrl = getBindingImageUrl(path)
    if (!imageUrl) return '无设备图片'

    return (
      <Image
        src={imageUrl}
        preview={{ src: imageUrl }}
        alt="设备图片"
        width={64}
        height={48}
        style={{ objectFit: 'cover', borderRadius: 4 }}
      />
    )
  }

  const renderBindingDetailField = (
    label: string,
    value: React.ReactNode,
  ) => (
    <div style={{ display: 'flex', gap: 12, minWidth: 0 }}>
      <span style={{ color: '#6b7280', flex: '0 0 120px' }}>{label}</span>
      <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{value}</span>
    </div>
  )

  const artifactColumns: ColumnsType<DeviceUpgradeArtifact> = [
    {
      title: '名称',
      dataIndex: 'name',
      width: 220,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '备注',
      dataIndex: 'description',
      width: 240,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '版本',
      dataIndex: 'version',
      width: 140,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '设备类型',
      dataIndex: 'device_type',
      width: 160,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '添加时间',
      dataIndex: 'uploaded_at',
      width: 180,
      render: renderDateTime,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: string) => {
        const isDisabled = status === 'disabled'
        return (
          <Tag color={isDisabled ? 'default' : 'green'}>
            {t(`admin:deviceUpgrade.statuses.${isDisabled ? 'disabled' : 'enabled'}`, {
              defaultValue: isDisabled ? '停用' : '启用',
            })}
          </Tag>
        )
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 250,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space wrap size={8}>
          {canUpdateDeviceUpgradeArtifact ? (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined aria-hidden />}
              onClick={() => openEditArtifactModal(record)}
            >
              编辑
            </Button>
          ) : null}
          {record.status === 'disabled' && canEnableDeviceUpgradeArtifact ? (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined aria-hidden />}
              onClick={() => void handleEnableArtifact(record.id)}
            >
              恢复使用
            </Button>
          ) : null}
          {record.status !== 'disabled' && canDisableDeviceUpgradeArtifact ? (
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined aria-hidden />}
              onClick={() => openDisableArtifactModal(record)}
            >
              停用
            </Button>
          ) : null}
          {canDeleteDeviceUpgradeArtifact ? (
            <Popconfirm
              title="确认删除该升级包？"
              okText="确定"
              cancelText="取消"
              onConfirm={() => void handleDeleteArtifact(record.id)}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined aria-hidden />}
              >
                删除
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ]

  const bindingColumns: ColumnsType<BindingListRow> = [
    {
      title: '设备名称',
      dataIndex: 'name',
      width: 180,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '所属项目',
      dataIndex: 'project_name',
      width: 180,
      ellipsis: true,
      render: (_, record) => renderPaginatedListText(renderBindingProject(record)),
    },
    {
      title: '当前版本',
      dataIndex: 'current_device_version',
      width: 140,
      ellipsis: true,
      render: (value, record) =>
        renderBindingCurrentVersion(
          typeof value === 'string' ? value : null,
          record.package_key,
        ),
    },
    {
      title: '设备系统',
      dataIndex: 'mender_device_system',
      width: 220,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '绑定状态',
      dataIndex: 'ota_binding_status',
      width: 120,
      render: renderStatusTag,
    },
    {
      title: '软件版本',
      dataIndex: 'ota_current_app_version',
      width: 140,
      ellipsis: true,
      render: (value, record) =>
        renderOtaAppVersion(
          typeof value === 'string' ? value : null,
          record.ota_version_status,
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space wrap size={8}>
          {canReadDeviceUpgradeBindingDetails ? (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined aria-hidden />}
              onClick={() => openBindingDetail(record)}
            >
              详情
            </Button>
          ) : null}
        </Space>
      ),
    },
  ]

  const batchColumns: ColumnsType<DeviceUpgradeBatch> = [
    {
      title: '批次',
      dataIndex: 'name',
      width: 220,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '备注',
      dataIndex: 'description',
      width: 240,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '目标版本',
      dataIndex: 'target_version',
      width: 140,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: renderStatusTag,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 180,
      render: renderDateTime,
    },
    { title: '目标数', dataIndex: 'target_device_count', width: 90 },
    { title: '成功', dataIndex: 'success_count', width: 80 },
    { title: '失败', dataIndex: 'failed_count', width: 80 },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space wrap size={8}>
          {canReadDeviceUpgradeBatchDetails ? (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined aria-hidden />}
              style={TABLE_ACTION_BUTTON_STYLE}
              onClick={() => openBatchDetail(record)}
            >
              详情
            </Button>
          ) : null}
          {canSyncDeviceUpgradeBatch ? (
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined aria-hidden />}
              loading={batchSyncBusyById[record.id]}
              disabled={!SYNCABLE_BATCH_STATUSES.has(record.status) || batchSyncBusyById[record.id]}
              style={TABLE_ACTION_BUTTON_STYLE}
              onClick={() => void handleSyncBatch(record.id)}
            >
              {batchSyncBusyById[record.id] ? '同步中' : '同步'}
            </Button>
          ) : null}
          {canCancelDeviceUpgradeBatch ? (
            <Popconfirm
              title="确认取消该升级批次？"
              okText="确定"
              cancelText="取消"
              onConfirm={() => void handleCancelBatch(record.id)}
            >
            <Button
              type="link"
              size="small"
              danger
              icon={<CloseCircleOutlined aria-hidden />}
              disabled={record.status === 'canceling' || TERMINAL_BATCH_STATUSES.has(record.status)}
              style={TABLE_ACTION_BUTTON_STYLE}
            >
              取消
            </Button>
          </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ]

  const batchDeviceColumns: ColumnsType<DeviceUpgradeBatchDevice> = [
    {
      title: '设备名称',
      dataIndex: 'device_name_snapshot',
      width: 180,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: renderBatchDeviceStatusTag,
    },
    {
      title: '上一版本',
      dataIndex: 'previous_app_version',
      width: 150,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '目标版本',
      dataIndex: 'target_version',
      width: 150,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '失败原因',
      dataIndex: 'failure_reason',
      width: 360,
      ellipsis: true,
      render: renderBatchDeviceFailureReason,
    },
  ]

  const selectableDeviceColumns: ColumnsType<SelectableDeviceTableRow> = [
    {
      title: '设备名称',
      dataIndex: 'name',
      width: 180,
      ellipsis: true,
      render: renderPaginatedListText,
    },
    {
      title: '所属项目',
      dataIndex: 'project_name',
      width: 180,
      ellipsis: true,
      render: (_, record) => renderPaginatedListText(renderBindingProject(record)),
    },
    {
      title: '软件版本',
      dataIndex: 'ota_current_app_version',
      width: 140,
      ellipsis: true,
      render: (value, record) =>
        renderOtaAppVersion(
          typeof value === 'string' ? value : null,
          record.ota_version_status,
        ),
    },
    {
      title: '当前版本',
      dataIndex: 'current_device_version',
      width: 140,
      ellipsis: true,
      render: (value, record) =>
        renderBindingCurrentVersion(
          typeof value === 'string' ? value : null,
          record.package_key,
        ),
    },
    {
      title: '设备系统',
      dataIndex: 'mender_device_system',
      width: 220,
      ellipsis: true,
      render: renderPaginatedListText,
    },
  ]

  const devicePickerPagination = buildPaginatedListPagination({
    current: devicePickerPage,
    pageSize: devicePickerPageSize,
    total: selectableDevicePage.total,
    showTotal: (total) => `共 ${total} 台可升级设备`,
    onChange: (page, pageSize) =>
      handleLocalPaginationChange(
        page,
        pageSize,
        devicePickerPageSize,
        setDevicePickerPage,
        setDevicePickerPageSize,
      ),
  })

  const updateSelectedUpgradeDevices = (nextSelectedKeys: React.Key[]) => {
    clearBatchConflict()
    const selectedKeySet = new Set(nextSelectedKeys.map(String))
    setSelectedUpgradeDevices((previous) => {
      const nextSelected = Object.fromEntries(
        Object.entries(previous).filter(([deviceId]) => selectedKeySet.has(deviceId)),
      ) as Record<string, SelectableUpgradeDevice>

      for (const device of selectableDevicePage.items) {
        if (selectedKeySet.has(device.id)) {
          nextSelected[device.id] = device
        }
      }

      return nextSelected
    })
  }

  const getConflictDeviceName = useCallback(
    (conflict: DeviceUpgradeBatchConflictDevice) =>
      conflict.device_name?.trim() || t('admin:deviceUpgrade.messages.unnamedDevice', {
        defaultValue: '未命名设备',
      }),
    [t],
  )

  const getConflictBatchNames = useCallback(
    (conflict: DeviceUpgradeBatchConflictDevice) =>
      conflict.running_batches
        .map((batch) => batch.batch_name?.trim() || t('admin:deviceUpgrade.messages.unnamedBatch', {
          defaultValue: '未命名批次',
        }))
        .join('、'),
    [t],
  )

  const batchConflictPreviewNames = useMemo(
    () => (batchConflict?.conflicts ?? [])
      .slice(0, BATCH_CONFLICT_PREVIEW_LIMIT)
      .map(getConflictDeviceName),
    [batchConflict, getConflictDeviceName],
  )
  const batchConflictRemainingCount = Math.max(
    0,
    (batchConflict?.conflict_count ?? 0) - batchConflictPreviewNames.length,
  )
  const filteredBatchConflicts = useMemo(() => {
    const keyword = normalizeSearchText(batchConflictKeyword).toLowerCase()
    const conflicts = batchConflict?.conflicts ?? []
    if (!keyword) return conflicts
    return conflicts.filter((conflict) =>
      [getConflictDeviceName(conflict), getConflictBatchNames(conflict)]
        .some((value) => value.toLowerCase().includes(keyword)),
    )
  }, [batchConflict, batchConflictKeyword, getConflictBatchNames, getConflictDeviceName])
  const paginatedBatchConflicts = useMemo(() => {
    const offset = (batchConflictPage - 1) * BATCH_CONFLICT_DETAIL_PAGE_SIZE
    return filteredBatchConflicts.slice(offset, offset + BATCH_CONFLICT_DETAIL_PAGE_SIZE)
  }, [batchConflictPage, filteredBatchConflicts])
  const batchConflictColumns: ColumnsType<DeviceUpgradeBatchConflictDevice> = [
    {
      title: t('admin:deviceUpgrade.messages.conflictDeviceName', {
        defaultValue: '设备名称',
      }),
      dataIndex: 'device_name',
      width: 260,
      ellipsis: true,
      render: (_, record) => renderPaginatedListText(getConflictDeviceName(record)),
    },
    {
      title: t('admin:deviceUpgrade.messages.conflictRunningBatches', {
        defaultValue: '所在批次',
      }),
      dataIndex: 'running_batches',
      ellipsis: true,
      render: (_, record) => renderPaginatedListText(getConflictBatchNames(record)),
    },
  ]

  const pageConfig = DEVICE_UPGRADE_SECTION_CONFIG[section]
  const pageTitle = t(pageConfig.titleKey, { defaultValue: pageConfig.defaultTitle })
  const sectionContent = section === 'bindings' ? (
    <Space direction="vertical" style={{ width: '100%' }}>
      <FilterBar data-testid="device-upgrade-binding-filter-panel">
        <FilterField label="设备名称">
          <Input
            allowClear
            value={bindingDeviceNameFilter}
            placeholder="搜索设备名称"
            onChange={(event) => setBindingDeviceNameFilter(event.target.value)}
            onPressEnter={applyBindingFilters}
          />
        </FilterField>
        <FilterField label="所属项目">
          <Select
            showSearch
            optionFilterProp="label"
            filterOption={filterSearchOptionByLabel}
            value={bindingProjectFilter}
            placeholder="全部项目"
            options={bindingProjectOptions}
            onChange={setBindingProjectFilter}
          />
        </FilterField>
        <FilterField label="状态">
          <Select
            value={bindingDeviceStatusFilter}
            placeholder="全部状态"
            options={bindingDeviceStatusOptions}
            onChange={setBindingDeviceStatusFilter}
          />
        </FilterField>
        <FilterField label="绑定状态">
          <Select
            value={bindingStatusFilter}
            placeholder="全部绑定状态"
            options={bindingStatusOptions}
            onChange={setBindingStatusFilter}
          />
        </FilterField>
        <FilterActions testId="device-upgrade-binding-filter-actions">
          <Button type="primary" icon={<SearchOutlined />} onClick={applyBindingFilters}>
            查询
          </Button>
          <Button onClick={resetBindingFilters}>
            重置
          </Button>
        </FilterActions>
      </FilterBar>
      <PaginatedListContainer>
        <PaginatedListToolbar data-testid="device-upgrade-binding-list-toolbar">
          {canSyncDeviceUpgradeDevices ? (
            <Button
              type="primary"
              loading={manualDeviceSyncBusy}
              disabled={manualDeviceSyncBusy}
              onClick={() => void handleSyncMenderDevices()}
            >
              {manualDeviceSyncBusy ? '同步中' : '同步 OTA 设备'}
            </Button>
          ) : null}
        </PaginatedListToolbar>
        <PaginatedListCard>
          <Table
            rowKey="rowKey"
            loading={loading}
            columns={bindingColumns}
            dataSource={filteredBindingRows}
            size={PAGINATED_LIST_TABLE_SIZE}
            tableLayout="fixed"
            scroll={{ x: BINDING_TABLE_SCROLL_X }}
            pagination={bindingPagination}
          />
        </PaginatedListCard>
      </PaginatedListContainer>
    </Space>
  ) : section === 'artifacts' ? (
    <Space direction="vertical" style={{ width: '100%' }}>
      <FilterBar data-testid="device-upgrade-artifact-filter-panel">
        <FilterField label="名称">
          <Input
            allowClear
            value={artifactKeyword}
            placeholder="搜索名称"
            onChange={(event) => setArtifactKeyword(event.target.value)}
            onPressEnter={applyArtifactFilters}
          />
        </FilterField>
        <FilterField label="版本">
          <Input
            allowClear
            value={artifactVersionFilter}
            placeholder="搜索版本"
            onChange={(event) => setArtifactVersionFilter(event.target.value)}
            onPressEnter={applyArtifactFilters}
          />
        </FilterField>
        <FilterField label="状态">
          <Select
            value={artifactStatusFilter}
            placeholder="全部状态"
            options={artifactStatusOptions}
            onChange={setArtifactStatusFilter}
          />
        </FilterField>
        <FilterActions testId="device-upgrade-artifact-filter-actions">
          <Button type="primary" icon={<SearchOutlined />} onClick={applyArtifactFilters}>
            查询
          </Button>
          <Button onClick={resetArtifactFilters}>
            重置
          </Button>
        </FilterActions>
      </FilterBar>
      <PaginatedListContainer>
        <PaginatedListToolbar data-testid="device-upgrade-artifact-list-toolbar">
          {canCreateDeviceUpgradeArtifact ? (
            <Button type="primary" onClick={() => setArtifactModalOpen(true)}>
              添加
            </Button>
          ) : null}
        </PaginatedListToolbar>
        <PaginatedListCard>
          <Table
            rowKey="id"
            loading={loading}
            columns={artifactColumns}
            dataSource={filteredArtifacts}
            size={PAGINATED_LIST_TABLE_SIZE}
            tableLayout="fixed"
            scroll={{ x: ARTIFACT_TABLE_SCROLL_X }}
            pagination={artifactPagination}
          />
        </PaginatedListCard>
      </PaginatedListContainer>
    </Space>
  ) : (
    <Space direction="vertical" style={{ width: '100%' }}>
      <FilterBar data-testid="device-upgrade-batch-filter-panel">
        <FilterField label="关键词">
          <Input
            allowClear
            value={batchKeyword}
            placeholder="搜索批次 / 目标版本 / 备注"
            onChange={(event) => setBatchKeyword(event.target.value)}
            onPressEnter={applyBatchFilters}
          />
        </FilterField>
        <FilterField label="状态">
          <Select
            allowClear
            value={batchStatusFilter}
            placeholder="全部状态"
            options={batchStatusOptions}
            onChange={setBatchStatusFilter}
          />
        </FilterField>
        <FilterActions testId="device-upgrade-batch-filter-actions">
          <Button type="primary" icon={<SearchOutlined />} onClick={applyBatchFilters}>
            查询
          </Button>
          <Button onClick={resetBatchFilters}>
            重置
          </Button>
        </FilterActions>
      </FilterBar>
      <PaginatedListContainer>
        <PaginatedListToolbar data-testid="device-upgrade-batch-list-toolbar">
          {canCreateDeviceUpgradeBatch ? (
            <Button type="primary" onClick={() => void openBatchModal()}>
              添加
            </Button>
          ) : null}
        </PaginatedListToolbar>
        <PaginatedListCard>
          <Table
            rowKey="id"
            loading={loading}
            columns={batchColumns}
            dataSource={filteredBatches}
            size={PAGINATED_LIST_TABLE_SIZE}
            tableLayout="fixed"
            scroll={{ x: BATCH_TABLE_SCROLL_X }}
            pagination={batchPagination}
          />
        </PaginatedListCard>
      </PaginatedListContainer>
    </Space>
  )

  if (!hasLoadedInitialData) {
    return null
  }

  return (
    <PageLayout
      title={pageTitle}
      breadcrumb={[
        { title: t('menu.operationsCenter', { defaultValue: '运维中心' }) },
        { title: pageTitle },
      ]}
    >
      {sectionContent}

      <Modal
        title="添加升级包"
        open={artifactModalOpen}
        maskClosable={false}
        onCancel={resetArtifactModalState}
        onOk={() => void createArtifact()}
        okButtonProps={{ disabled: !artifactMetadata || artifactInspecting }}
      >
        <Form
          form={artifactForm}
          layout="vertical"
        >
          <Alert
            showIcon
            type={artifactMetadata ? 'success' : 'info'}
            message={artifactMetadata ? '已自动读取升级包信息' : '先选择 .mender 文件，系统会自动读取升级包信息'}
          />
          <Upload
            beforeUpload={handleArtifactBeforeUpload}
            fileList={artifactFile ? [{ uid: 'artifact-file', name: artifactFile.name, status: 'done' }] : []}
            maxCount={1}
            accept=".mender"
            disabled={artifactInspecting}
            onRemove={() => {
              setArtifactFile(null)
              setArtifactMetadata(null)
              artifactForm.resetFields()
            }}
          >
            <Button icon={<UploadOutlined />} loading={artifactInspecting} disabled={artifactInspecting}>选择 .mender 文件</Button>
          </Upload>
          {artifactMetadata ? (
            <>
              <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item name="package_type" label="类型" rules={[{ required: true }]}>
                <Select
                  disabled
                  options={[
                    {
                      value: 'application',
                      label: t('admin:deviceUpgrade.packageTypes.application', { defaultValue: '应用包' }),
                    },
                    {
                      value: 'model',
                      label: t('admin:deviceUpgrade.packageTypes.model', { defaultValue: '模型包' }),
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item name="version" label="版本" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item name="device_type" label="设备类型" rules={[{ required: true }]}>
                <Input disabled placeholder="从 .mender 文件自动读取" />
              </Form.Item>
              <Form.Item name="artifact_name" label="OTA Artifact 名称">
                <Input disabled placeholder="mosquito-device-orangepi4pro-abc123" />
              </Form.Item>
              <Form.Item name="description" label="备注" rules={[{ max: DESCRIPTION_MAX_LENGTH }]}>
                <Input.TextArea autoSize={{ minRows: 3 }} maxLength={DESCRIPTION_MAX_LENGTH} showCount />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title="编辑备注"
        open={Boolean(editArtifactTarget)}
        maskClosable={false}
        onCancel={closeEditArtifactModal}
        onOk={() => void handleUpdateArtifactDescription()}
        okText="保存"
      >
        <Form form={editArtifactForm} layout="vertical">
          <Form.Item name="description" label="备注" rules={[{ max: DESCRIPTION_MAX_LENGTH }]}>
            <Input.TextArea autoSize={{ minRows: 3 }} maxLength={DESCRIPTION_MAX_LENGTH} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="停用升级包"
        open={Boolean(disableArtifactTarget)}
        maskClosable={false}
        onCancel={closeDisableArtifactModal}
        onOk={() => void handleDisableArtifact()}
        okText="停用"
        okButtonProps={{ danger: true }}
      >
        <Form form={disableArtifactForm} layout="vertical">
          <Form.Item
            name="description"
            label="备注"
            rules={[
              { required: true, whitespace: true, message: '请输入停用备注' },
              { max: DESCRIPTION_MAX_LENGTH },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 3 }} maxLength={DESCRIPTION_MAX_LENGTH} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新建升级批次"
        open={batchModalOpen}
        width={960}
        maskClosable={false}
        styles={{ body: BATCH_MODAL_BODY_STYLE }}
        onCancel={closeBatchModal}
        onOk={() => void createBatch()}
        confirmLoading={batchSubmitting}
        okButtonProps={{ disabled: selectedDeviceIds.length === 0 || batchSubmitting }}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item
            name="name"
            label="批次名称"
            rules={[
              { required: true, whitespace: true, message: '请输入批次名称' },
              { max: BATCH_NAME_MAX_LENGTH },
            ]}
          >
            <Input maxLength={BATCH_NAME_MAX_LENGTH} />
          </Form.Item>
          <Form.Item name="artifact_id" label="升级包" rules={[{ required: true }]}>
            <Select
              disabled={batchArtifactsLoading}
              loading={batchArtifactsLoading}
              placeholder="请选择升级包"
              options={artifacts
                .filter((artifact) => artifact.status === 'uploaded')
                .map((artifact) => {
                  const description = artifact.description?.trim()
                  return {
                    label: description ? `${artifact.name} ${description}` : artifact.name,
                    value: artifact.id,
                  }
                })}
            />
          </Form.Item>
          <Form.Item label="目标设备" required>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {!selectedArtifactId ? (
                <Alert
                  type="info"
                  showIcon
                  message="请先选择升级包，系统会按升级包设备类型筛出可升级设备。"
                />
              ) : null}
              <FilterBar data-testid="device-upgrade-target-device-filter-panel">
                <FilterField label="设备名称">
                  <Input
                    allowClear
                    disabled={!selectedArtifactId}
                    value={devicePickerKeyword}
                    placeholder="搜索设备名称"
                    onChange={(event) => {
                      setDevicePickerKeyword(event.target.value)
                      setDevicePickerPage(1)
                    }}
                  />
                </FilterField>
                <FilterField label="项目名称">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={filterSearchOptionByLabel}
                    disabled={!selectedArtifactId}
                    value={devicePickerProjectIds}
                    placeholder="全部项目"
                    maxTagCount="responsive"
                    options={projectOptions.map((project) => ({
                      label: project.name,
                      value: project.id,
                    }))}
                    onChange={(value) => {
                      setDevicePickerProjectIds(value)
                      setDevicePickerPage(1)
                    }}
                  />
                </FilterField>
                <FilterActions testId="device-upgrade-target-device-filter-actions">
                  <Button
                    type="primary"
                    icon={<SearchOutlined aria-hidden />}
                    disabled={!selectedArtifactId}
                    loading={devicePickerLoading}
                    onClick={applyDevicePickerFilters}
                  >
                    查询
                  </Button>
                  <Button disabled={!selectedArtifactId} onClick={resetDevicePickerFilters}>
                    重置
                  </Button>
                </FilterActions>
              </FilterBar>
              <Table
                rowKey="id"
                size={PAGINATED_LIST_TABLE_SIZE}
                loading={devicePickerLoading}
                columns={selectableDeviceColumns}
                dataSource={selectableDevicePage.items}
                tableLayout="fixed"
                scroll={{ x: DEVICE_PICKER_TABLE_SCROLL_X }}
                locale={{
                  emptyText: selectedArtifactId
                    ? '暂无符合该升级包设备类型和绑定条件的设备。'
                    : '请先选择升级包',
                }}
                pagination={devicePickerPagination}
                rowSelection={{
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedDeviceIds,
                  onChange: updateSelectedUpgradeDevices,
                  getCheckboxProps: () => ({
                    disabled: !selectedArtifactId,
                  }),
                }}
              />
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space wrap>
                  <span>已选 {selectedDeviceIds.length} 台设备</span>
                  <Button
                    size="small"
                    disabled={selectedDeviceIds.length === 0}
                    onClick={() => {
                      clearBatchConflict()
                      setSelectedUpgradeDevices({})
                    }}
                  >
                    清空已选
                  </Button>
                </Space>
              </Space>
            </Space>
          </Form.Item>
          <Form.Item name="description" label="备注" rules={[{ max: DESCRIPTION_MAX_LENGTH }]}>
            <Input.TextArea autoSize={{ minRows: 3 }} maxLength={DESCRIPTION_MAX_LENGTH} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('admin:deviceUpgrade.messages.batchConflictModalTitle', {
          defaultValue: '创建升级批次失败',
        })}
        open={batchConflictPromptOpen && Boolean(batchConflict)}
        width={560}
        maskClosable={false}
        onCancel={clearBatchConflict}
        footer={[
          <Button
            key="details"
            onClick={() => {
              setBatchConflictKeyword('')
              setBatchConflictPage(1)
              setBatchConflictDetailOpen(true)
            }}
          >
            {t('admin:deviceUpgrade.messages.viewConflictDetails', {
              defaultValue: '查看冲突详情',
            })}
          </Button>,
          <Button key="close" type="primary" onClick={clearBatchConflict}>
            {t('admin:deviceUpgrade.messages.closeConflictDetails', {
              defaultValue: '关闭',
            })}
          </Button>,
        ]}
      >
        {batchConflict ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text>
              {t('admin:deviceUpgrade.messages.batchConflictSummary', {
                selectedCount: batchConflict.selected_count,
                conflictCount: batchConflict.conflict_count,
                defaultValue: `选择的 ${batchConflict.selected_count} 台设备中，有 ${batchConflict.conflict_count} 台已在其他运行中的升级批次里，不能重复升级。`,
              })}
            </Typography.Text>
            <Typography.Text>
              {t('admin:deviceUpgrade.messages.batchConflictPreviewLabel', {
                defaultValue: '冲突设备：',
              })}
              {batchConflictPreviewNames.join('、')}
              {batchConflictRemainingCount > 0
                ? t('admin:deviceUpgrade.messages.batchConflictRemaining', {
                    count: batchConflict.conflict_count,
                    defaultValue: ` 等 ${batchConflict.conflict_count} 台设备`,
                  })
                : null}
            </Typography.Text>
            <Typography.Text type="secondary">
              {t('admin:deviceUpgrade.messages.batchConflictAdvice', {
                defaultValue: '请移除这些设备后重新创建，或者查看对应升级批次。',
              })}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={t('admin:deviceUpgrade.messages.conflictDetailsTitle', {
          defaultValue: '冲突设备详情',
        })}
        open={batchConflictDetailOpen}
        width={760}
        maskClosable={false}
        footer={null}
        onCancel={() => setBatchConflictDetailOpen(false)}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input
            allowClear
            value={batchConflictKeyword}
            placeholder={t('admin:deviceUpgrade.messages.conflictSearchPlaceholder', {
              defaultValue: '搜索设备名称 / 所在批次',
            })}
            onChange={(event) => {
              setBatchConflictKeyword(event.target.value)
              setBatchConflictPage(1)
            }}
          />
          <Table
            rowKey="device_id"
            size={PAGINATED_LIST_TABLE_SIZE}
            columns={batchConflictColumns}
            dataSource={paginatedBatchConflicts}
            tableLayout="fixed"
            pagination={{
              current: batchConflictPage,
              pageSize: BATCH_CONFLICT_DETAIL_PAGE_SIZE,
              total: filteredBatchConflicts.length,
              showSizeChanger: false,
              showTotal: (total) => t('admin:deviceUpgrade.messages.conflictDetailsTotal', {
                total,
                defaultValue: `共 ${total} 台冲突设备`,
              }),
              onChange: (page) => setBatchConflictPage(page),
            }}
          />
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setBatchConflictDetailOpen(false)}>
              {t('admin:deviceUpgrade.messages.closeConflictDetails', {
                defaultValue: '关闭',
              })}
            </Button>
          </div>
        </Space>
      </Modal>

      <Modal
        title={selectedBindingDetail ? `设备详情：${selectedBindingDetail.name || selectedBindingDetail.id}` : '设备详情'}
        open={bindingDetailOpen}
        width={820}
        footer={null}
        maskClosable={false}
        onCancel={closeBindingDetail}
      >
        {selectedBindingDetail ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Card title="设备详情">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {renderBindingDetailField('设备 ID', selectedBindingDetail.id)}
                {renderBindingDetailField('设备名称', selectedBindingDetail.name || '-')}
                {renderBindingDetailField('所属项目', renderBindingProject(selectedBindingDetail))}
                {renderBindingDetailField(
                  '设备图片',
                  renderBindingImage(selectedBindingDetail.installation_image_path),
                )}
                {renderBindingDetailField('当前版本', renderNullableText(selectedBindingDetail.current_device_version))}
                {renderBindingDetailField(
                  '应用版本',
                  renderOtaAppVersion(
                    selectedBindingDetail.ota_current_app_version,
                    selectedBindingDetail.ota_version_status,
                  ),
                )}
                {renderBindingDetailField('版本确认时间', renderDateTime(selectedBindingDetail.ota_version_observed_at))}
                {renderBindingDetailField('在线状态', renderBindingDeviceStatusTag(selectedBindingDetail.status))}
              </Space>
            </Card>
            <Card title="OTA 详情">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {renderBindingDetailField('OTA 设备 ID', renderNullableText(selectedBindingDetail.mender_device_id))}
                {renderBindingDetailField('硬件 ID', renderNullableText(selectedBindingDetail.mender_hardware_id))}
                {renderBindingDetailField('设备系统', renderNullableText(selectedBindingDetail.mender_device_system))}
                {renderBindingDetailField('OTA 最近上报时间', renderDateTime(selectedBindingDetail.mender_last_seen_at))}
                {renderBindingDetailField('绑定状态', renderStatusTag(selectedBindingDetail.ota_binding_status))}
                {renderBindingDetailField('最后库存同步时间', renderDateTime(selectedBindingDetail.ota_last_inventory_sync_at))}
              </Space>
            </Card>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={selectedBatchDetail ? `批次详情：${selectedBatchDetail.name}` : '批次详情'}
        open={batchDetailOpen}
        width={1180}
        footer={null}
        maskClosable={false}
        onCancel={closeBatchDetail}
      >
        {selectedBatchDetail ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ color: '#6b7280' }}>创建时间</span>
              <span>{renderDateTime(selectedBatchDetail.created_at)}</span>
            </div>
            <FilterBar data-testid="device-upgrade-batch-device-filter-panel">
              <FilterField label="设备名称">
                <Input
                  allowClear
                  value={batchDeviceKeyword}
                  placeholder="搜索设备名称"
                  onChange={(event) => {
                    setBatchDeviceKeyword(event.target.value)
                  }}
                />
              </FilterField>
              <FilterField label="状态">
                <Select
                  allowClear
                  value={batchDeviceStatusFilter}
                  placeholder="全部状态"
                  options={batchDeviceStatusOptions}
                  onChange={(value) => {
                    setBatchDeviceStatusFilter(value)
                  }}
                />
              </FilterField>
              <FilterActions testId="device-upgrade-batch-device-filter-actions">
                <Button type="primary" icon={<SearchOutlined />} onClick={applyBatchDeviceFilters}>
                  查询
                </Button>
                <Button onClick={resetBatchDeviceFilters}>
                  重置
                </Button>
              </FilterActions>
            </FilterBar>
            <Table
              rowKey="id"
              loading={batchDeviceLoading}
              columns={batchDeviceColumns}
              dataSource={batchDeviceData.items}
              size={PAGINATED_LIST_TABLE_SIZE}
              tableLayout="fixed"
              scroll={{ x: BATCH_DEVICE_TABLE_SCROLL_X }}
              pagination={batchDevicePagination}
            />
          </Space>
        ) : null}
      </Modal>
    </PageLayout>
  )
}

export default DeviceUpgradeManagement
